import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { CalendarToday, Person, AccessTime, ArrowBack, FilterList } from '@mui/icons-material';
import { blogPosts, categories } from '../data/blogPosts';

const BlogList = () => {
    const [selectedCategory, setSelectedCategory] = useState('Tous');
    const [searchTerm, setSearchTerm] = useState('');

    const filteredPosts = useMemo(() => {
        return blogPosts.filter(post => {
            const matchesCategory = selectedCategory === 'Tous' || post.category === selectedCategory;
            const matchesSearch = !searchTerm || 
                post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [selectedCategory, searchTerm]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
            <Helmet>
                <title>Blog Technique - Netikash Developer Portal</title>
                <meta name="description" content="Blog technique Netikash : articles, tutoriels et guides sur l'API, les paiements, la sécurité et l'intégration." />
                <link rel="canonical" href="https://dev.netikash.com/blog" />
            </Helmet>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl lg:text-5xl font-bold mb-4">Blog Technique</h1>
                        <p className="text-xl text-white/90 max-w-3xl mx-auto">
                            Articles, tutoriels et guides pour maîtriser l'API Netikash
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Filters */}
                <div className="mb-8">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex items-center space-x-4 mb-4">
                            <FilterList className="text-amber-600" />
                            <h3 className="font-semibold text-gray-900">Filtrer par catégorie</h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                        selectedCategory === category
                                            ? 'bg-amber-500 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Rechercher un article..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                </div>

                {/* Featured Posts */}
                {selectedCategory === 'Tous' && !searchTerm && (
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Articles en Vedette</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {blogPosts.filter(p => p.featured).slice(0, 2).map(post => (
                                <Link
                                    key={post.id}
                                    to={`/blog/${post.slug}`}
                                    className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
                                >
                                    <div className="h-48 bg-gradient-to-r from-amber-400 to-orange-400"></div>
                                    <div className="p-6">
                                        <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold mb-3">
                                            {post.category}
                                        </span>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{post.title}</h3>
                                        <p className="text-gray-600 mb-4">{post.excerpt}</p>
                                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                                            <div className="flex items-center space-x-1">
                                                <Person fontSize="small" />
                                                <span>{post.author}</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <CalendarToday fontSize="small" />
                                                <span>{new Date(post.date).toLocaleDateString('fr-FR')}</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <AccessTime fontSize="small" />
                                                <span>{post.readTime}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* All Posts */}
                <section>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">
                        {selectedCategory === 'Tous' && !searchTerm ? 'Tous les Articles' : 'Résultats'}
                    </h2>
                    {filteredPosts.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                            <p className="text-gray-600">Aucun article trouvé.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredPosts.map(post => (
                                <Link
                                    key={post.id}
                                    to={`/blog/${post.slug}`}
                                    className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
                                >
                                    <div className="h-40 bg-gradient-to-r from-amber-400 to-orange-400"></div>
                                    <div className="p-6">
                                        <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold mb-3">
                                            {post.category}
                                        </span>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{post.title}</h3>
                                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                                            <div className="flex items-center space-x-1">
                                                <CalendarToday fontSize="small" />
                                                <span>{new Date(post.date).toLocaleDateString('fr-FR')}</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <AccessTime fontSize="small" />
                                                <span>{post.readTime}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

const BlogPost = ({ slug }) => {
    const post = blogPosts.find(p => p.slug === slug);

    if (!post) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
                    <Link to="/blog" className="text-amber-600 hover:underline">
                        Retour au blog
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{post.title} - Blog Netikash</title>
                <meta name="description" content={post.excerpt} />
                <link rel="canonical" href={`https://dev.netikash.com/blog/${post.slug}`} />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <Link
                        to="/blog"
                        className="inline-flex items-center space-x-2 text-amber-600 hover:text-amber-700 mb-8"
                    >
                        <ArrowBack fontSize="small" />
                        <span>Retour au blog</span>
                    </Link>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="h-64 bg-gradient-to-r from-amber-400 to-orange-400"></div>
                        <div className="p-8 md:p-12">
                            <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold mb-4">
                                {post.category}
                            </span>
                            <h1 className="text-4xl font-bold text-gray-900 mb-6">{post.title}</h1>
                            <div className="flex items-center space-x-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
                                <div className="flex items-center space-x-2">
                                    <Person fontSize="small" />
                                    <span>{post.author}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <CalendarToday fontSize="small" />
                                    <span>{new Date(post.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <AccessTime fontSize="small" />
                                    <span>{post.readTime} de lecture</span>
                                </div>
                            </div>
                            <div className="prose max-w-none">
                                <p className="text-xl text-gray-700 mb-6">{post.excerpt}</p>
                                <div className="text-gray-700 leading-relaxed">
                                    <p className="mb-4">
                                        Cet article sera bientôt disponible avec le contenu complet. En attendant, 
                                        consultez notre documentation pour obtenir toutes les informations nécessaires.
                                    </p>
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-6">
                                        <p className="text-amber-900">
                                            <strong>Note :</strong> Le contenu complet de cet article sera ajouté prochainement. 
                                            Pour plus d'informations, visitez notre <Link to="/documentation" className="underline">documentation</Link>.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-12 pt-8 border-t border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-4">Tags</h3>
                                <div className="flex flex-wrap gap-2">
                                    {post.tags.map(tag => (
                                        <span
                                            key={tag}
                                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </article>
            </div>
        </>
    );
};

const Blog = () => {
    const { slug } = useParams();

    if (slug) {
        return <BlogPost slug={slug} />;
    }

    return <BlogList />;
};

export default Blog;